import React from 'react';
import { styled } from '@mui/material/styles';
import { Card } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * InputCard - Pure styled wrapper component for input + button groups
 * Style 6B: Material Outlined design
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Input and button components
 * @param {Object} props.sx - Custom styles
 * @param {boolean} props.fullWidth - Full width container
 * @param {number|string} props.maxWidth - Maximum width (theme spacing units or CSS value). Default: 100% (takes parent width)
 */
const StyledInputCard = styled(Card)(({ theme, variant }) => ({
    height: '100%',
    display: 'flex',
    ...(variant === 'input' ? {
        flex: 'none', // Don't expand for input cards
    } : {
        flex: 1, // Allow expansion for output cards in grid layouts
    }),
    flexDirection: 'column',
    minWidth: 0, // Allow shrinking below content size
    border: `1px solid ${variant === 'output' ? theme.palette.primary.main : theme.palette.divider}`,
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.12)',
        ...(variant === 'output' && {
            borderColor: theme.palette.primary.dark,
        }),
    },
    '&>.MuiCardHeader-root': {
        textAlign: 'left',
        flexShrink: 0, // Keep header at top, don't shrink
        '& .MuiCardHeader-action': {
            marginRight: theme.spacing(0),
        },
    },
    '&>.MuiCardContent-root': {
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(2),
        ...(variant === 'input' ? {
            flex: 'none', // Don't expand for input cards
        } : {
            flex: 1, // Allow expansion for output cards in grid layouts
        }),
        overflow: 'auto', // Allow scrolling if content overflows
    },
    '&>.MuiCardActions-root': {
        flexShrink: 0, // Keep actions at bottom, don't shrink
        marginTop: 'auto', // Push to bottom
        padding: theme.spacing(2),
        '&:last-child': {
            paddingBottom: theme.spacing(2),
        },
    },
}));

export const InputCard = ({ children, sx, fullWidth = false, maxWidth, padding = 0, variant = 'input', ...props }) => {
    const theme = useTheme();

    return (
        <StyledInputCard
            variant={variant}
            sx={{
                width: '100%',
                ...(maxWidth && {
                    maxWidth: typeof maxWidth === 'number' ? theme.spacing(maxWidth) : maxWidth,
                }),
                ...sx,
                padding: padding,
            }}
            {...props}
        >
            {children}
        </StyledInputCard>
    );
};

